import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Loader2, CopyCheck, Clock, DollarSign } from 'lucide-react';
import { fmtDate, fmtWindow, hoursLabel, money, addDays } from '../../../lib/scheduleFormat';

const windowParts = (window) => (window || '').split('–');

// Kitchen hours are copied FIRST and the shifts placed inside them, so a normal
// week lands in one action. Nothing is dropped silently: every trim and every
// skip is reported against its date.
const CopyWeekDialog = ({ open, weekStart, onClose, onCopy, busy, result }) => {
  const [source, setSource] = useState(addDays(weekStart, -7));
  const [includeHours, setIncludeHours] = useState(true);
  const [includeShifts, setIncludeShifts] = useState(true);
  const totals = result?.totals || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="copy-week-dialog">
        <DialogHeader>
          <DialogTitle>Copy a week into this one</DialogTitle>
          <DialogDescription>
            Onto the week of {fmtDate(weekStart)}. Hours are booked first, then the shifts are placed
            inside them. Anything that no longer fits is listed rather than dropped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2" data-testid="copy-week-options">
            {[-7, -14, -21].map((offset) => {
              const candidate = addDays(weekStart, offset);
              return (
                <Button
                  key={offset}
                  type="button"
                  variant={source === candidate ? 'default' : 'outline'}
                  className={source === candidate ? 'bg-amber-600 hover:bg-amber-700' : ''}
                  onClick={() => setSource(candidate)}
                  data-testid={`copy-week-source-${candidate}`}
                >
                  Week of {fmtDate(candidate)}
                </Button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-4 rounded-lg border border-gray-200 p-3" data-testid="copy-week-what">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-900">
              <Checkbox checked={includeHours} onCheckedChange={(v) => setIncludeHours(Boolean(v))}
                data-testid="copy-week-include-hours" />
              Kitchen hours
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-900">
              <Checkbox checked={includeShifts} onCheckedChange={(v) => setIncludeShifts(Boolean(v))}
                data-testid="copy-week-include-shifts" />
              Staff shifts
            </label>
            {includeHours && (
              <p className="w-full text-xs text-gray-500">
                Copied hours keep their times, notes and landlord status, and the delivery dates they serve
                move by the same number of days. These are hours you pay for.
              </p>
            )}
          </div>

          {result && (
            <div className="space-y-2" data-testid="copy-week-result">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <span className="text-sm font-medium text-gray-900">
                  {totals.bookings || 0} day{totals.bookings === 1 ? '' : 's'} of hours booked
                </span>
                <span className="flex items-center gap-1 text-sm font-medium text-gray-900">
                  <Clock className="h-4 w-4 text-amber-700" /> {hoursLabel(totals.hours)}
                </span>
                <span className="flex items-center gap-1 text-sm font-medium text-gray-900"
                  data-testid="copy-week-cost">
                  <DollarSign className="h-4 w-4 text-amber-700" /> {money(totals.cost)}
                </span>
                <span className="text-sm text-gray-700">
                  {result.created} shift{result.created === 1 ? '' : 's'} copied
                </span>
              </div>

              {result.dates?.length === 0 && (
                <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600"
                  data-testid="copy-week-nothing">
                  Nothing changed — that week had nothing that could be placed here.
                </p>
              )}

              {result.dates?.map((day) => (
                <div key={day.date} className="rounded-lg border border-gray-200 p-3"
                  data-testid={`copy-week-date-${day.date}`}>
                  <p className="text-sm font-medium text-gray-900">{fmtDate(day.date)}</p>
                  <div className="mt-1.5 space-y-1 text-xs">
                    {day.booked.map((row, i) => {
                      const [from, to] = windowParts(row.window);
                      return (
                        <p key={`b${i}`} className="text-emerald-800" data-testid={`copy-booked-${day.date}-${i}`}>
                          <Badge variant="outline" className="mr-2 border-emerald-300 bg-emerald-50 text-[10px] text-emerald-800">
                            hours
                          </Badge>
                          {fmtWindow(from, to)} · {hoursLabel(row.hours)} · {money(row.cost)}
                        </p>
                      );
                    })}
                    {day.trimmed.map((row, i) => {
                      const [from, to] = windowParts(row.window);
                      const [wasFrom, wasTo] = windowParts(row.requested_window);
                      return (
                        <p key={`t${i}`} className="text-amber-900" data-testid={`copy-trimmed-${day.date}-${i}`}>
                          <Badge variant="outline" className="mr-2 border-amber-300 bg-amber-50 text-[10px] text-amber-900">
                            trimmed
                          </Badge>
                          {fmtWindow(wasFrom, wasTo)} → {fmtWindow(from, to)} · {hoursLabel(row.hours)} ·
                          {' '}{money(row.cost)} — {row.reason}
                        </p>
                      );
                    })}
                    {day.hours_skipped.map((row, i) => {
                      const [from, to] = windowParts(row.requested_window);
                      return (
                        <p key={`s${i}`} className="text-slate-700" data-testid={`copy-hours-skipped-${day.date}-${i}`}>
                          <Badge variant="outline" className="mr-2 border-slate-300 bg-slate-50 text-[10px] text-slate-700">
                            no hours
                          </Badge>
                          {fmtWindow(from, to)} — {row.reason}
                        </p>
                      );
                    })}
                    {day.shifts_created.map((row, i) => {
                      const [from, to] = windowParts(row.window);
                      return (
                        <p key={`c${i}`} className="text-gray-700" data-testid={`copy-shift-${day.date}-${i}`}>
                          <Badge variant="outline" className="mr-2 bg-white text-[10px]">shift</Badge>
                          {row.user_name} · {row.position_code} · {fmtWindow(from, to)}
                        </p>
                      );
                    })}
                    {day.shifts_skipped.map((row, i) => {
                      const [from, to] = windowParts(row.window);
                      return (
                        <p key={`k${i}`} className="text-amber-900" data-testid={`copy-week-skipped-${day.date}-${i}`}>
                          <Badge variant="outline" className="mr-2 border-amber-300 bg-amber-50 text-[10px] text-amber-900">
                            shift skipped
                          </Badge>
                          {row.user_name} · {fmtWindow(from, to)} — {row.reason}
                        </p>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="copy-week-close">
            {result ? 'Done' : 'Cancel'}
          </Button>
          <Button
            onClick={() => onCopy(source, { includeHours, includeShifts })}
            disabled={busy || (!includeHours && !includeShifts)}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="copy-week-confirm"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CopyCheck className="mr-2 h-4 w-4" />}
            {includeHours && includeShifts ? 'Copy hours & shifts' : includeHours ? 'Copy hours' : 'Copy shifts'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopyWeekDialog;
